import { TDynamicFormConfig } from "@/features/Edit/dynamic-edit-form";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { Control } from "react-hook-form";

import { AutocompleteAsyncField } from "@/features/FormCard/Fields";
import {
  Option,
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER,
} from "../../../features/Edit/common";
import { z } from "zod";
import { AssetClass2 } from "./types/asset_class2";

function ConcentratedAssetField(control: Control<any>) {
  return (
    <AutocompleteAsyncField
      name="concentrated_asset"
      label="Actif concentré du client"
      control={control}
      base_url="l1/l1_portfolios"
      id_column="l1_portfolio_id"
      column_name="name"
    />
  );
}

export const concentrated_asset_conf = {
  label: "Actif concentré du client",
  validator: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        // we recv / send as string the data
        z.string(),

        // for the autocomplete, the form state is an object
        z.object({
          text: z.string(),
          id: z.number(),
          account_type: z.string(),
          currency: z.string(),
        }),
      ]),
    )
    .optional(),
  field: ConcentratedAssetField,
};

// Example of a dynamic form config
export const dynamicFormConfig: TDynamicFormConfig = {
  id: z.number(),
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .transform((val) => val.toUpperCase()),
  isin: z
    .string()
    .refine(
      (val) => val === "" || val === null || val.length === 12,
      "L'ISIN doit comporter  12 caractères",
    )
    .nullable()
    .optional(),
  code: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  strategy_id: z.coerce.number().optional().nullable(),
  asset_class_2: {
    key_config: {
      validator: {
        asset_class_1: z.string().nullable().optional(),
        asset_class_2: z.string().nullable().optional(),
      },
      primary_key: "asset_class_1",
      secondary_key: "asset_class_2",
    },
    select: {
      equity: {
        [AssetClass2.CARREFOUR]: false,
        [AssetClass2.EUROPEAN_STOCK]: false,
        [AssetClass2.EUROPEAN_STOCK_FUND]: false,
        [AssetClass2.US_STOCK]: false,
        [AssetClass2.US_STOCK_FUND]: false,
        [AssetClass2.ASIAN_STOCK]: false,
        [AssetClass2.WORLD_STOCK_FUND]: false,
        [AssetClass2.EMERGING_STOCK_FUND]: false,
        [AssetClass2.THAMEATICS_FUND]: false,
      },
      fixed_income: {
        [AssetClass2.FIXED_TERM_BOND]: AssetClass2.FIXED_TERM_BOND,
        [AssetClass2.PERPETUAL_BOND]: AssetClass2.PERPETUAL_BOND,
        [AssetClass2.BOND]: false,
      },
      structured_product: {
        [AssetClass2.STRUCTURED_NOTES_HOTTINGUER]:
          AssetClass2.STRUCTURED_NOTES_HOTTINGUER,
        [AssetClass2.STRUTURED_NOTES_OTHER]: AssetClass2.STRUTURED_NOTES_OTHER,
      },
      diversified_investments: {
        [AssetClass2.ALTERNATIVE_FUND]: false,
        [AssetClass2.MULTI_ASSET_FUND]: false,
      },
      cash: {
        [AssetClass2.CASH]: false,
        [AssetClass2.MONETARY_FUND]: false,
        [AssetClass2.TERM_ACCOUNT]: AssetClass2.TERM_ACCOUNT,
        [AssetClass2.UNALLOCATED_FEES]: false,
      },
      private_equity: {
        [AssetClass2.BUYOUT]: AssetClass2.BUYOUT,
        [AssetClass2.GROWTH]: AssetClass2.GROWTH,
        [AssetClass2.GP_STAKE]: AssetClass2.GP_STAKE,
        [AssetClass2.SECONDARY]: AssetClass2.SECONDARY,
        [AssetClass2.VENTURE]: AssetClass2.VENTURE,
      },
      private_debt: {
        [AssetClass2.DEBT_FUND]: AssetClass2.DEBT_FUND,
        [AssetClass2.DIRECT_PRIVATE_DEBT]: AssetClass2.DIRECT_PRIVATE_DEBT,
      },
      real_estate: {
        [AssetClass2.RE_CORE_FUND]: AssetClass2.RE_CORE_FUND,
        [AssetClass2.RE_VALUE_ADDED_FUND]: AssetClass2.RE_VALUE_ADDED_FUND,
        [AssetClass2.LIVE_REAL_ESTATE]: AssetClass2.LIVE_REAL_ESTATE,
      },
    } as const,
    section: {
      // Cash
      [AssetClass2.TERM_ACCOUNT]: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Private debt
      [AssetClass2.DIRECT_PRIVATE_DEBT]: {
        loan_amount_granted: {
          label: "Montant du prêt accordé",
          validator: z.coerce.number().optional().nullable(),
        },
        loan_rate: {
          label: "Taux du prêt",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      [AssetClass2.DEBT_FUND]: {
        loan_amount_granted: {
          label: "Montant du prêt accordé",
          validator: z.coerce.number().optional().nullable(),
        },
        loan_rate: {
          label: "Taux du prêt",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Private Equity
      [AssetClass2.BUYOUT]: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        committment_end_date: {
          label: "Fin de la période d'engagement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      [AssetClass2.GP_STAKE]: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        committment_end_date: {
          label: "Fin de la période d'engagement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      [AssetClass2.GROWTH]: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        committment_end_date: {
          label: "Fin de la période d'engagement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      [AssetClass2.SECONDARY]: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        committment_end_date: {
          label: "Fin de la période d'engagement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      [AssetClass2.VENTURE]: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        committment_end_date: {
          label: "Fin de la période d'engagement",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      // Real Estate
      [AssetClass2.LIVE_REAL_ESTATE]: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      [AssetClass2.RE_VALUE_ADDED_FUND]: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      [AssetClass2.RE_CORE_FUND]: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Obligations
      [AssetClass2.FIXED_TERM_BOND]: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      [AssetClass2.PERPETUAL_BOND]: {
        /*maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },*/
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Structured products
      [AssetClass2.STRUTURED_NOTES_OTHER]: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
      },
      [AssetClass2.STRUCTURED_NOTES_HOTTINGUER]: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
      },
    },
  },
};

const DynamicFormSchema = z.object(dynamicFormConfig);
export const dynamicFormFields = DynamicFormSchema.keyof().enum;

export const DEFAULT_ASSET_CLASS_OPTIONS: Option[] =
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER(dynamicFormConfig);

export const dynamicNewFormConfigWithoutId: Omit<TDynamicFormConfig, "id"> =
  (() => {
    const { id, ...configWithoutId } = dynamicFormConfig;
    return configWithoutId;
  })();
